import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PractitionerDialog from './PractitionerDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import { getPractitioners, deletePractitioner, Practitioner } from './practitionerService';

const PractitionerManager: React.FC = () => {
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedPractitioner, setSelectedPractitioner] = useState<Practitioner | null>(null);
  
  const fetchPractitioners = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPractitioners();
      setPractitioners(data.practitioners || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load practitioners');
      console.error('Error loading practitioners:', err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchPractitioners();
  }, []);
  
  const handleAddClick = () => {
    setSelectedPractitioner(null);
    setOpenDialog(true);
  };
  
  const handleEditClick = (practitioner: Practitioner) => {
    setSelectedPractitioner(practitioner);
    setOpenDialog(true);
  };
  
  const handleDeleteClick = (practitioner: Practitioner) => {
    setSelectedPractitioner(practitioner);
    setOpenDeleteDialog(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!selectedPractitioner) return;
    
    try {
      setLoading(true);
      await deletePractitioner(selectedPractitioner.id);
      setOpenDeleteDialog(false);
      await fetchPractitioners(); // Refresh the list
    } catch (err: any) {
      setError(err.message || 'Failed to delete practitioner');
      console.error('Error deleting practitioner:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDialogClose = (refreshNeeded: boolean) => {
    setOpenDialog(false);
    if (refreshNeeded) {
      fetchPractitioners();
    }
  };
  
  const copyClinikoId = (id: string) => {
    navigator.clipboard.writeText(id)
      .then(() => {
        alert('Cliniko ID copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy Cliniko ID:', err);
        alert('Failed to copy Cliniko ID. Please copy it manually: ' + id);
      });
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Practitioner Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          Add Practitioner
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading && !practitioners.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Cliniko ID</TableCell>
                <TableCell>Specialties</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {practitioners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No practitioners found. Click "Add Practitioner" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                practitioners.map((practitioner) => (
                  <TableRow key={practitioner.id}>
                    <TableCell>{practitioner.name}</TableCell>
                    <TableCell>{practitioner.title}</TableCell>
                    <TableCell>
                      {practitioner.cliniko_id}
                      <IconButton
                        size="small"
                        onClick={() => copyClinikoId(practitioner.cliniko_id)}
                        title="Copy Cliniko ID"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell>{practitioner.specialties}</TableCell>
                    <TableCell>
                      <Chip 
                        label={practitioner.is_active ? 'Active' : 'Inactive'} 
                        color={practitioner.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit practitioner">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(practitioner)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete practitioner">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(practitioner)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {openDialog && (
        <PractitionerDialog
          open={openDialog}
          onClose={handleDialogClose}
          practitioner={selectedPractitioner}
        />
      )}
      
      {openDeleteDialog && selectedPractitioner && (
        <DeleteConfirmationDialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onConfirm={handleDeleteConfirm}
          practitionerName={selectedPractitioner.name}
        />
      )}
    </Box>
  );
};

export default PractitionerManager;